/**
 * WorldMapView.jsx — MapLibre GL JS · air/world scope
 *
 * Renders plane SVG markers for flying sessions with great-circle arc paths
 * between origin and destination airports. No OSRM — arcs are computed
 * client-side from the greatCircleArc() helper in JourneyQueueMapLibre.js.
 *
 * Default center: mid-Atlantic (JFK→LAS corridor), zoom 3.
 *
 * Known routes rendered:
 *   JFK (40.6413, -73.7781) → LAS (36.0840, -115.1537)   NYC → Las Vegas
 *   BOS (42.3656, -71.0096) → LAS (36.0840, -115.1537)   Boston → Las Vegas
 *   LAS → JFK, LAS → BOS   (return legs)
 *
 * queueRegistryRef entries get an `airJq` property for air-scoped sessions,
 * mirroring the `cityJq` pattern in CityMapView.
 *
 * Props:
 *   sessions          — array of serialized map session objects
 *   queueRegistryRef  — useRef(new Map()) shared across all views
 *   isActive          — boolean; triggers map resize on tab show
 *
 * Dependencies: window.maplibregl must exist (loaded by Index.jsx).
 */

import React, { useEffect, useRef, useCallback, useState } from "react";
import {
  JourneyQueueMapLibre,
  greatCircleArc,
  snapAirPositionToArc,
  haversineKm as haversineDist,
  SNAP_AIRPORTS   as AIRPORTS,
  SNAP_KNOWN_ROUTES as KNOWN_ROUTES,
} from "../../../utils/JourneyQueueMapLibre.js";
import { ALERT_COLORS } from "../../../utils/JourneyQueueSvgIcons.js";

// Airport labels shown on the world map (superset of SNAP_AIRPORTS coords)
const AIRPORTS_LABELED = {
  JFK: { ...AIRPORTS.JFK,                                  label: "New York – JFK"              },
  BOS: { ...AIRPORTS.BOS,                                  label: "Boston Logan"                 },
  LAS: { ...AIRPORTS.LAS,                                  label: "Las Vegas – Harry Reid"       },
  ATL: { code: "ATL", lat: 33.6407,  lng: -84.4277,       label: "Atlanta – Hartsfield-Jackson" },
  LAX: { code: "LAX", lat: 33.9425,  lng: -118.4081,      label: "Los Angeles – LAX"            },
};

const OPENFREEMAP_STYLE = "https://tiles.openfreemap.org/styles/liberty";

// World-view center: mid-continent so JFK, BOS, and LAS all fit at zoom 3
// MapLibre uses [lng, lat] GeoJSON order (not [lat, lng] like Leaflet)
const WORLD_CENTER = [-90.0, 30.0];

// ── Component ────────────────────────────────────────────────────────────────

export default function WorldMapView({ sessions = [], queueRegistryRef, isActive, onCitySelect }) {
  const containerRef    = useRef(null);
  const mapRef          = useRef(null);
  const markersAddedRef = useRef(new Set());
  const arcLayersRef    = useRef(new Set());   // track added GeoJSON source IDs
  const [graticule, setGraticule] = useState(false);

  // Keep a stable ref so the click handler inside the closed-over map init
  // always calls the latest callback even if the prop reference changes.
  const onCitySelectRef = useRef(onCitySelect);
  useEffect(() => { onCitySelectRef.current = onCitySelect; }, [onCitySelect]);

  // ── Map init ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!window.maplibregl || !containerRef.current) return;

    const maplibregl = window.maplibregl;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style:     OPENFREEMAP_STYLE,
      center:    [-90.0, 30.0],
      zoom:      3,
      pitch:     0,
      bearing:   0,
      attributionControl: false,
    });

    map.addControl(new maplibregl.NavigationControl({ showCompass: true }),  "top-right");
    map.addControl(new maplibregl.AttributionControl({ compact: true }),     "bottom-left");

    // Airport labels — pass the ref so click handlers always have the latest callback
    map.on("style.load", () => {
      _addAirportLabels(map, onCitySelectRef);
      _addKnownRouteArcs(map, arcLayersRef, false);
    });

    mapRef.current = map;

    return () => {
      queueRegistryRef.current.forEach((entry) => {
        if (entry?.airJq) {
          entry.airJq.removeFromMap();
          entry.airJq = null;
        }
      });
      markersAddedRef.current.clear();
      arcLayersRef.current.clear();
      map.remove();
      mapRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Resize on tab activate ──────────────────────────────────────────────

  useEffect(() => {
    if (!isActive || !mapRef.current) return;
    const t = setTimeout(() => mapRef.current?.resize(), 50);
    return () => clearTimeout(t);
  }, [isActive]);

  // ── Sync air markers ────────────────────────────────────────────────────

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const airSessions = sessions.filter((s) => s.scope === "air");
    const activeIds   = new Set(airSessions.map((s) => String(s.id)));

    // Remove stale
    markersAddedRef.current.forEach((id) => {
      if (!activeIds.has(id)) {
        const entry = queueRegistryRef.current.get(id);
        if (entry?.airJq) {
          entry.airJq.removeFromMap();
          entry.airJq = null;
        }
        markersAddedRef.current.delete(id);
      }
    });

    // Add / update
    airSessions.forEach((sessionData) => {
      const id    = String(sessionData.id);
      const entry = queueRegistryRef.current.get(id) || {};

      // Snap position to nearest great-circle arc so the marker sits on the arc
      // rather than at the linearly-interpolated seeded coordinate.
      const arcPt          = snapAirPositionToArc(sessionData.position);
      const correctedPos   = arcPt
        ? { ...sessionData.position, lat: arcPt[1], lng: arcPt[0] }
        : sessionData.position;
      const correctedSession = { ...sessionData, position: correctedPos };

      if (!markersAddedRef.current.has(id)) {
        const jq = new JourneyQueueMapLibre(correctedSession);
        entry.airJq = jq;
        queueRegistryRef.current.set(id, entry);
        markersAddedRef.current.add(id);

        const doAdd = () => jq.addToMap(map, null);   // null dest — no OSRM for air
        if (map.isStyleLoaded()) {
          doAdd();
        } else {
          map.once("style.load", doAdd);
        }
      } else if (entry?.airJq) {
        entry.airJq.updatePosition(correctedPos, sessionData.alert_color);
      }
    });
  }, [sessions]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Graticule toggle ────────────────────────────────────────────────────

  const toggleGraticule = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const next = !graticule;
    setGraticule(next);

    // Toggle the arc layer transparency as a proxy for the grid lines feature
    arcLayersRef.current.forEach((sourceId) => {
      const layerId = `arc-layer-${sourceId}`;
      if (map.getLayer(layerId)) {
        map.setPaintProperty(layerId, "line-opacity", next ? 0.9 : 0.35);
      }
    });
  }, [graticule]);

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div style={styles.root}>
      <div ref={containerRef} style={styles.map} />

      {/* Toolbar */}
      <div style={styles.toolbar}>
        <button
          onClick={toggleGraticule}
          title="Highlight routes"
          style={{
            ...styles.toolBtn,
            background: graticule ? "rgba(99,102,241,0.9)" : "rgba(7,9,15,0.82)",
            borderColor: graticule ? "#818CF8" : "#374151",
          }}
        >
          ✈️
        </button>
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        {["JFK", "BOS", "LAS"].map((code) => (
          <div key={code} style={styles.legendRow}>
            <span style={styles.legendDot} />
            <span style={styles.legendLabel}>{AIRPORTS_LABELED[code].label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Private helpers ─────────────────────────────────────────────────────────

function _addAirportLabels(map, onCitySelectRef) {
  const maplibregl = window.maplibregl;

  Object.values(AIRPORTS_LABELED).forEach((ap) => {
    const el = document.createElement("div");
    const clickable = !!onCitySelectRef?.current;
    el.style.cssText = `
      background: rgba(7,9,15,0.88);
      border: 1.5px solid #6366F1;
      border-radius: 5px;
      padding: 2px 7px 2px 7px;
      font: 700 10px/1.5 monospace;
      color: #A5B4FC;
      white-space: nowrap;
      pointer-events: ${clickable ? "auto" : "none"};
      cursor: ${clickable ? "pointer" : "default"};
      transition: border-color 0.15s, background 0.15s, color 0.15s;
      user-select: none;
    `;
    // Show city-switch hint alongside the IATA code
    el.textContent = clickable ? `${ap.code} ↗` : ap.code;
    el.title       = clickable ? `Switch City view to ${ap.label}` : ap.label;

    if (clickable) {
      el.addEventListener("mouseenter", () => {
        el.style.borderColor = "#A5B4FC";
        el.style.background  = "rgba(99,102,241,0.28)";
        el.style.color       = "#C7D2FE";
      });
      el.addEventListener("mouseleave", () => {
        el.style.borderColor = "#6366F1";
        el.style.background  = "rgba(7,9,15,0.88)";
        el.style.color       = "#A5B4FC";
      });
      el.addEventListener("click", () => {
        onCitySelectRef.current?.(ap.code);
      });
    }

    new maplibregl.Marker({ element: el, anchor: "bottom" })
      .setLngLat([ap.lng, ap.lat])
      .addTo(map);
  });
}

function _addKnownRouteArcs(map, arcLayersRef, highlighted = false) {
  KNOWN_ROUTES.forEach(({ from, to }) => {
    const origin = AIRPORTS[from];
    const dest   = AIRPORTS[to];
    if (!origin || !dest) return;

    const arc        = greatCircleArc(origin, dest, 400);
    const sourceId   = `gc-arc-${from}-${to}`;
    const layerId    = `arc-layer-${sourceId}`;

    if (map.getSource(sourceId)) return;

    map.addSource(sourceId, {
      type: "geojson",
      data: {
        type:     "Feature",
        geometry: { type: "LineString", coordinates: arc },
      },
    });

    map.addLayer({
      id:     layerId,
      type:   "line",
      source: sourceId,
      paint: {
        "line-color":   "#6366F1",
        "line-width":   1.5,
        "line-opacity": highlighted ? 0.9 : 0.35,
        "line-dasharray": [4, 4],
      },
    });

    arcLayersRef.current.add(sourceId);
  });
}

// ── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  root: {
    position:      "relative",
    flex:          1,
    display:       "flex",
    flexDirection: "column",
    minHeight:     0,
    background:    "#07090F",
  },
  map: {
    flex:      1,
    minHeight: 0,
  },
  toolbar: {
    position:      "absolute",
    top:           12,
    left:          12,
    display:       "flex",
    flexDirection: "column",
    gap:           6,
    zIndex:        10,
  },
  toolBtn: {
    width:          34,
    height:         34,
    borderRadius:   8,
    border:         "1px solid #374151",
    color:          "#E0E0F0",
    fontSize:       16,
    cursor:         "pointer",
    display:        "flex",
    alignItems:     "center",
    justifyContent: "center",
    transition:     "border-color 0.15s, background 0.15s",
  },
  legend: {
    position:    "absolute",
    bottom:      36,
    right:       12,
    background:  "rgba(7,9,15,0.82)",
    border:      "1px solid #1F2937",
    borderRadius: 8,
    padding:     "8px 12px",
    zIndex:      10,
    display:     "flex",
    flexDirection: "column",
    gap:         4,
  },
  legendRow: {
    display:    "flex",
    alignItems: "center",
    gap:        6,
  },
  legendDot: {
    width:        8,
    height:       8,
    borderRadius: "50%",
    background:   "#6366F1",
    flexShrink:   0,
  },
  legendLabel: {
    fontSize: 11,
    color:    "#9CA3AF",
    fontFamily: "system-ui,sans-serif",
  },
};
